import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";

// Route-level code splitting: each page ships as its own chunk so the first
// load only downloads the shell + landing page instead of the whole app.
const Live = lazy(() => import("@/pages/Live"));
const Emergency = lazy(() => import("@/pages/Emergency"));
const Prepare = lazy(() => import("@/pages/Prepare"));
const Guides = lazy(() => import("@/pages/Guides"));
const Guide = lazy(() => import("@/pages/Guide"));
const Faq = lazy(() => import("@/pages/Faq"));
const Storm = lazy(() => import("@/pages/Storm"));
const FoodAlerts = lazy(() => import("@/pages/FoodAlerts"));
const Water = lazy(() => import("@/pages/Water"));
const Organisations = lazy(() => import("@/pages/Organisations"));
const BusinessApp = lazy(() => import("@/pages/business/BusinessApp"));
const BizAuthCallback = lazy(() => import("@/pages/business/BusinessApp").then((m) => ({ default: m.BizAuthCallback })));
const BusinessContinuity = lazy(() => import("@/pages/business/BusinessInfo").then((m) => ({ default: m.BusinessContinuity })));
const BusinessSample = lazy(() => import("@/pages/business/BusinessInfo").then((m) => ({ default: m.BusinessSample })));

// Google sign-in returns with #session_id — handle it before normal routes (read from useLocation so it re-renders).
function SessionGate({ children }) {
  const { hash } = useLocation();
  return hash?.includes("session_id=") ? <BizAuthCallback /> : children;
}
const Worksheets = lazy(() => import("@/pages/Worksheets"));
const Tools = lazy(() => import("@/pages/Tools"));
const MyPlan = lazy(() => import("@/pages/MyPlan"));
const PlanOnboarding = lazy(() => import("@/pages/plan/Onboarding"));
const PlanSection = lazy(() => import("@/pages/plan/PlanSection"));
const EmergencyMode = lazy(() => import("@/pages/plan/EmergencyMode"));
const DocumentPack = lazy(() => import("@/pages/plan/DocumentPack"));
const DrillPlan = lazy(() => import("@/pages/plan/DrillPlan"));
const Local = lazy(() => import("@/pages/Local"));
const Downloads = lazy(() => import("@/pages/Downloads"));
const KitBuilder = lazy(() => import("@/pages/KitBuilder"));
const Readiness = lazy(() => import("@/pages/Readiness"));
const FamilyComms = lazy(() => import("@/pages/FamilyComms"));
const Community = lazy(() => import("@/pages/CommunityBusiness").then((m) => ({ default: m.Community })));
const Business = lazy(() => import("@/pages/CommunityBusiness").then((m) => ({ default: m.Business })));
const Verify = lazy(() => import("@/pages/Verify"));
const Report = lazy(() => import("@/pages/Report"));
const ReportSection = lazy(() => import("@/pages/ReportSection"));
const Updates = lazy(() => import("@/pages/Updates"));
const Packs = lazy(() => import("@/pages/Packs"));
const PackDetail = lazy(() => import("@/pages/PackDetail"));
const Resilience = lazy(() => import("@/pages/Resilience"));
const Lite = lazy(() => import("@/pages/Lite"));
const ConfirmEmail = lazy(() => import("@/pages/ConfirmEmail"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const StaticPage = lazy(() => import("@/pages/StaticPage"));
const Contact = lazy(() => import("@/pages/Contact"));
const Evidence = lazy(() => import("@/pages/Evidence"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

const STATIC = ["about", "how-we-source-information", "editorial-standards", "live-data-sources", "corrections", "privacy", "disclaimer", "terms", "accessibility"];

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" data-testid="route-loading">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-navy" aria-label="Loading" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppProvider>
          <AuthProvider>
            <Suspense fallback={<RouteFallback />}>
              <SessionGate>
              <Routes>
                <Route path="/lite" element={<Lite />} />
                <Route path="/my-plan/emergency" element={<EmergencyMode />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="live" element={<Live />} />
                  <Route path="emergency" element={<Emergency />} />
                  <Route path="what-to-do-now" element={<Emergency />} />
                  <Route path="prepare" element={<Prepare />} />
                  <Route path="guides" element={<Guides />} />
                  <Route path="guides/:slug" element={<Guide />} />
                  <Route path="my-plan" element={<MyPlan />} />
                  <Route path="my-plan/start" element={<PlanOnboarding />} />
                  <Route path="my-plan/s/:key" element={<PlanSection />} />
                  <Route path="my-plan/print" element={<DocumentPack />} />
                  <Route path="my-plan/drill" element={<DrillPlan />} />
                  <Route path="local" element={<Local />} />
                  <Route path="downloads" element={<Downloads />} />
                  <Route path="kit" element={<KitBuilder />} />
                  <Route path="readiness" element={<Readiness />} />
                  <Route path="family" element={<FamilyComms />} />
                  <Route path="community" element={<Community />} />
                  <Route path="business" element={<Business />} />
                  <Route path="verify" element={<Verify />} />
                  <Route path="report" element={<Report />} />
                  <Route path="report/:slug" element={<ReportSection />} />
                  <Route path="updates" element={<Updates />} />
                  <Route path="packs" element={<Packs />} />
                  <Route path="packs/:slug" element={<PackDetail />} />
                  <Route path="local/resilience" element={<Resilience />} />
                  <Route path="confirm-email" element={<ConfirmEmail />} />
                  <Route path="unsubscribe" element={<Unsubscribe />} />
                  {STATIC.map((s) => <Route key={s} path={s} element={<StaticPage slug={s} />} />)}
                  <Route path="contact" element={<Contact />} />
                  <Route path="evidence" element={<Evidence />} />
                  <Route path="faq" element={<Faq />} />
                  <Route path="storm" element={<Storm />} />
                  <Route path="food-alerts" element={<FoodAlerts />} />
                  <Route path="water" element={<Water />} />
                  <Route path="organisations" element={<Organisations />} />
                  <Route path="business/continuity" element={<BusinessContinuity />} />
                  <Route path="business/sample" element={<BusinessSample />} />
                  <Route path="business/app" element={<BusinessApp />} />
                  <Route path="worksheets" element={<Worksheets />} />
                  <Route path="worksheets/:slug" element={<Worksheets />} />
                  <Route path="tools" element={<Tools />} />
                  <Route path="tools/:tool" element={<Tools />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
              </SessionGate>
            </Suspense>
          </AuthProvider>
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
